/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  Copy,
  Layers,
  Palette
} from 'lucide-react';
import { accountService } from '../services/accountService';
import { subAccountService } from '../services/subAccountService';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import { useAuth } from '../context/AuthContext';
import toastService from '../utils/toastService';
import MultiSelect from '../components/MultiSelect';
import { DAISY_THEMES, FIELD_LIMITS } from '../utils/constants';
import { validateTextField } from '../utils/validation';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const ROUTE_SUB_ACCOUNTS = '/sub-accounts';

const CloneSubAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: sourceId } = useParams();
  const { user } = useAuth();
  const [sourceSubAccount, setSourceSubAccount] = useState(location.state?.sub ?? null);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    account_id: user?.scope === 'account' ? user.accountUID : '',
    name: '',
    slug: '',
    externalSubAccountId: '',
    serviceRequestTypeIds: [],
  });
  const [subAccountBrandingOpen, setSubAccountBrandingOpen] = useState(false);
  const [availableServiceRequestTypes, setAvailableServiceRequestTypes] = useState([]);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [sourceDefaultSRTIds, setSourceDefaultSRTIds] = useState([]);

  useEffect(() => {
    if (user?.scope !== 'platform') {
      navigate(ROUTE_SUB_ACCOUNTS, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadAccounts = async () => {
      if (user?.scope === 'account') {
        setForm(f => ({ ...f, account_id: user.accountUID }));
        return;
      }
      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setParentAccounts(data.data || data || []);
      } catch (err) {
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    loadAccounts();
  }, [user]);

  useEffect(() => {
    const loadSourceAndOptions = async () => {
      try {
        setLoading(true);
        let source = sourceSubAccount;

        if (!source?.accountId) {
          const { data } = await subAccountService.getSubAccounts(null, { limit: 100, page: 1 });
          source = (data.data || []).find(sub => sub.id === sourceId);
          if (!source) {
            throw new Error(`Source ${SUB_ACCOUNT_LABEL.singularLower} not found`);
          }
          setSourceSubAccount(source);
        }

        if (!defaultsApplied && source?.accountId) {
          try {
            const { data: cloneOptions } = await subAccountService.getCloneOptions(source.accountId, sourceId);
            setSourceDefaultSRTIds(cloneOptions.serviceRequestTypeIds || []);
          } catch (err) {
            // Non-fatal — multi-select just starts empty
          } finally {
            setDefaultsApplied(true);
          }
        }
      } catch (err) {
        toastService.error(err.message || 'Failed to load clone details');
        navigate(ROUTE_SUB_ACCOUNTS);
      } finally {
        setLoading(false);
      }
    };
    loadSourceAndOptions();
  }, [navigate, sourceId, sourceSubAccount, defaultsApplied]);

  useEffect(() => {
    const loadTypesForTargetAccount = async () => {
      if (!form.account_id) {
        setAvailableServiceRequestTypes([]);
        setForm(f => ({ ...f, serviceRequestTypeIds: [] }));
        return;
      }
      try {
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ accountUid: form.account_id, limit: 100 });
        const types = data.data || [];
        setAvailableServiceRequestTypes(types);
        const availableIds = new Set(types.map(t => t.uid));
        setForm(f => ({ ...f, serviceRequestTypeIds: sourceDefaultSRTIds.filter(id => availableIds.has(id)) }));
      } catch (err) {
        setAvailableServiceRequestTypes([]);
      }
    };
    loadTypesForTargetAccount();
  }, [form.account_id, sourceDefaultSRTIds]);

  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setForm(f => ({ ...f, name, slug }));
    if (fieldErrors.name) {
      setFieldErrors(prev => ({ ...prev, name: validateTextField(name, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
    }
  };

  const handleBlur = (field, value) => {
    if (field === 'name') {
      const error = validateTextField(value, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
      setFieldErrors(prev => ({ ...prev, name: error }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.account_id) { errors.account_id = `Parent ${ACCOUNT_LABEL.singularLower} is required`; }
    if (!form.name.trim()) { errors.name = `${SUB_ACCOUNT_LABEL.singular} name is required`; }
    const nameError = validateTextField(form.name, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (nameError) { errors.name = nameError; }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }

    try {
      setSubmitting(true);
      await subAccountService.cloneSubAccount({
        sourceSubAccountId: sourceId,
        parentAccountId: form.account_id,
        name: form.name.trim(),
        slug: form.slug.trim(),
        externalSubAccountId: form.externalSubAccountId.trim() || undefined,
        categories: [],
        serviceRequestTypeIds: form.serviceRequestTypeIds,
      });
      toastService.success(`${SUB_ACCOUNT_LABEL.singular} cloned successfully`);
      navigate(ROUTE_SUB_ACCOUNTS);
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || `Failed to clone ${SUB_ACCOUNT_LABEL.singularLower}`));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${
      fieldErrors[field] ? 'border-error ring-1 ring-error/20' : 'border-base-300'
    }`;

  const selectClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:bg-base-200/50 disabled:text-base-content/40 ${
      fieldErrors[field] ? 'border-error ring-1 ring-error/20' : 'border-base-300'
    }`;

  if (loading) {
    return (
      <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
        <div className="h-4 w-32 bg-base-300/60 rounded animate-pulse"></div>
        <div className="glass-card rounded-2xl p-6 border border-base-content/10">
          <div className="h-7 w-48 bg-base-300/60 rounded mb-2 animate-pulse"></div>
          <div className="h-4 w-80 bg-base-200 rounded animate-pulse"></div>
        </div>
        <div className="glass-card rounded-2xl p-6 border border-base-content/10 space-y-4">
          <div className="h-5 w-40 bg-base-300/60 rounded animate-pulse"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="h-10 bg-base-200/60 rounded-xl animate-pulse"></div>
            <div className="h-10 bg-base-200/60 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {SUB_ACCOUNT_LABEL.plural}
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                Clone Record
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Clone {SUB_ACCOUNT_LABEL.singular}
            </h1>
            <p className="text-xs text-base-content/50">
              Duplicate settings, {SERVICE_REQUEST_LABEL.singularLower} types, and configurations to create a new {SUB_ACCOUNT_LABEL.singularLower}.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Source Sub-Account Banner */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-3">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Layers size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Source {SUB_ACCOUNT_LABEL.singular}
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Reference</span>
          </div>

          <div className="p-3.5 rounded-xl bg-base-200/50 border border-base-300 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">
                <Layers size={16} />
              </div>
              <div>
                <p className="text-sm font-bold text-base-content">{sourceSubAccount?.name}</p>
                <p className="text-xs text-base-content/50">{sourceSubAccount?.accountName || `Parent ${ACCOUNT_LABEL.singular}`}</p>
              </div>
            </div>
            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md bg-base-300/60 text-base-content/60">
              Read-Only
            </span>
          </div>
        </div>

        {/* Organization & Scope Selection */}
        {user?.scope === 'platform' && (
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
              <Building2 size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Target Organization
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="parentAccount" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                  Parent {ACCOUNT_LABEL.singular} *
                </label>
                <select
                  id="parentAccount"
                  className={selectClass('account_id')}
                  value={form.account_id}
                  onChange={(e) => {
                    setForm(f => ({ ...f, account_id: e.target.value }));
                    if (fieldErrors.account_id) setFieldErrors(prev => ({ ...prev, account_id: null }));
                  }}
                >
                  <option value="">— Select Parent {ACCOUNT_LABEL.singular} —</option>
                  {parentAccounts.map((acc) => (
                    <option
                      key={acc.id}
                      value={acc.id}
                      disabled={acc.status === 'inactive'}
                    >
                      {acc.name}{acc.status === 'inactive' ? ' (Inactive)' : ''}
                    </option>
                  ))}
                </select>
                {fieldErrors.account_id && (
                  <p className="text-error text-xs font-medium">{fieldErrors.account_id}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New Sub-Account Details */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Layers size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              New {SUB_ACCOUNT_LABEL.singular} Details
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                {SUB_ACCOUNT_LABEL.singular} Name *
              </label>
              <input
                id="name"
                type="text"
                maxLength={FIELD_LIMITS.DEFAULT_TEXT}
                placeholder="e.g. Downtown Service Center"
                className={inputClass('name')}
                value={form.name}
                onChange={handleNameChange}
                onBlur={(e) => handleBlur('name', e.target.value)}
              />
              {fieldErrors.name && (
                <p className="text-error text-xs font-medium">{fieldErrors.name}</p>
              )}
            </div>

            {/* External Sub-Account ID */}
            <div className="space-y-1.5">
              <label htmlFor="externalSubAccountId" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                External {SUB_ACCOUNT_LABEL.singular} ID
              </label>
              <input
                id="externalSubAccountId"
                type="text"
                maxLength={255}
                placeholder="Optional"
                className={inputClass('externalSubAccountId')}
                value={form.externalSubAccountId}
                onChange={e => setForm(f => ({ ...f, externalSubAccountId: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Service Request Types */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4 relative z-20">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Layers size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                {SERVICE_REQUEST_LABEL.singular} Types
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          <div className="space-y-1.5">
            <MultiSelect
              label={`Assigned ${SERVICE_REQUEST_LABEL.singular} Types`}
              searchPlaceholder={`Search ${SERVICE_REQUEST_LABEL.singularLower} types...`}
              placeholder={form.account_id ? `Select ${SERVICE_REQUEST_LABEL.singularLower} types (optional)` : `Select a parent ${ACCOUNT_LABEL.singularLower} first`}
              options={availableServiceRequestTypes.map(t => ({ id: t.uid, key: t.name }))}
              selected={form.serviceRequestTypeIds}
              onChange={(ids) => setForm(f => ({ ...f, serviceRequestTypeIds: ids }))}
            />
          </div>
        </div>

        {/* Sub-Account Branding Section (Read-only from source) */}
        <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden">
          <button
            type="button"
            onClick={() => setSubAccountBrandingOpen(o => !o)}
            className="w-full flex items-center justify-between p-5 sm:p-6 bg-transparent hover:bg-base-200/40 transition text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                <Palette size={16} />
              </div>
              <div>
                <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                  Source Branding
                </h2>
                <p className="text-xs text-base-content/50">
                  Branding settings inherited from source {SUB_ACCOUNT_LABEL.singularLower}
                </p>
              </div>
            </div>
            <ChevronRight size={18} className={`text-base-content/30 transition-transform ${subAccountBrandingOpen ? 'rotate-90' : ''}`} />
          </button>

          {subAccountBrandingOpen && (
            <div className="p-5 sm:p-6 border-t border-base-content/10 space-y-6 bg-base-200/20">
              {/* Experience Theme */}
              <div>
                <label className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block mb-2">
                  Experience Theme
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {DAISY_THEMES.map((theme) => {
                    const isActive = (sourceSubAccount?.theme || 'light') === theme;
                    return (
                      <div
                        key={theme}
                        className={`relative flex items-center gap-2 p-1.5 rounded-xl select-none border-2 cursor-not-allowed opacity-90 ${
                          isActive
                            ? 'border-primary bg-primary/10 shadow-sm'
                            : 'border-base-300 bg-base-100 opacity-40'
                        }`}
                      >
                        <div
                          data-theme={theme}
                          className="bg-base-100 grid grid-cols-2 gap-0.5 rounded-lg p-1 shadow-sm border border-base-content/5 w-7 h-7 shrink-0 place-items-center relative"
                        >
                          <div className="bg-base-content w-1 h-1 rounded-full" />
                          <div className="bg-primary w-1 h-1 rounded-full" />
                          <div className="bg-secondary w-1 h-1 rounded-full" />
                          <div className="bg-accent w-1 h-1 rounded-full" />
                          {isActive && (
                            <div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-primary drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className={`text-[11px] font-bold capitalize truncate leading-none pr-1 ${isActive ? 'text-primary' : 'text-base-content/70'}`}>
                          {theme}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Brand Logo URL */}
              {sourceSubAccount?.logoUrl && (
                <div>
                  <label className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block mb-2">
                    Brand Logo
                  </label>
                  <div className="mt-2 flex flex-col gap-1">
                    <img
                      src={sourceSubAccount.logoUrl}
                      alt="Logo preview"
                      className="h-20 w-20 object-contain rounded-xl border border-base-300 bg-base-100 p-1"
                    />
                  </div>
                </div>
              )}

              {/* Favicon URL */}
              {sourceSubAccount?.faviconUrl && (
                <div>
                  <label className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block mb-2">
                    Favicon
                  </label>
                  <div className="mt-2 flex flex-col gap-1">
                    <img
                      src={sourceSubAccount.faviconUrl}
                      alt="Favicon preview"
                      className="h-10 w-10 object-contain rounded-lg border border-base-300 bg-base-100 p-0.5"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2 relative z-0">
          <button
            type="button"
            onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Cloning {SUB_ACCOUNT_LABEL.singular}...</span>
              </>
            ) : (
              <>
                <Copy size={16} />
                <span>Clone {SUB_ACCOUNT_LABEL.singular}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CloneSubAccount;
